import React, { useEffect, useState } from 'react';
import {
  ChevronLeft,
  Edit2,
  KeyRound,
  Unlink,
  Save,
  Send,
  X,
  AlertTriangle,
  Loader2,
  Lock,
  ShieldOff,
  QrCode,
} from 'lucide-react';
import toast from 'react-hot-toast';

import caregiverService, { getActiveSponsorId } from '../../services/caregiverService';
import CaregiverStatusBadge from './statusBadge';
import CaregiverSetupQR from './CaregiverSetupQR';
import ResetPinModal from './ResetPinModal';
import DeactivateLinkConfirm from './DeactivateLinkConfirm';
import RelationshipTypeSelect from './RelationshipTypeSelect';

// Matches AddCaregiverModal — keep input rules consistent across create + edit.
const sanitizePhoneInput = (raw) => (raw || '').replace(/[^\d+\-() .]/g, '');
const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidPhone = (value) => (value.match(/\d/g) || []).length >= 7;

/**
 * Caregiver detail screen (FECOA-1371) with inline edit (FECOA-1372).
 *
 * Renders inside the CaregiversTab (no route change) for snappy back/forward.
 * The parent passes the participant + caregiver context as props.
 */
const CaregiverDetail = ({ caregiverID, participantID, studyCode, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [caregiver, setCaregiver] = useState(null);
  const [linkRow, setLinkRow] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showResetPin, setShowResetPin] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [showSetupQr, setShowSetupQr] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cgRes, listRes] = await Promise.all([
        caregiverService.get(caregiverID),
        caregiverService.listForParticipant(participantID),
      ]);
      setCaregiver(cgRes);
      const row = (listRes.caregivers || []).find(
        (r) => r.caregiver?.id === caregiverID || r.caregiver?.id === cgRes?.id
      );
      setLinkRow(row || null);
      setForm({
        first_name: cgRes.first_name || '',
        last_name: cgRes.last_name || '',
        email: cgRes.email || '',
        phone: cgRes.phone || '',
        relationship_type: row?.relationship_type || '',
      });
    } catch (err) {
      console.error('failed to load caregiver detail', err);
      toast.error('Failed to load caregiver.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caregiverID, participantID]);

  const status = linkRow?.status || (caregiver?.is_active ? 'Active' : 'Deactivated');
  const linkActive = linkRow ? linkRow.is_active : false;
  const isLocked = status === 'Locked';
  const isDeactivated = status === 'Deactivated';
  const isActiveStatus = status === 'Active';
  const isPendingFirstLogin = status === 'Pending First Login';
  const canResendInvite = isPendingFirstLogin && linkActive && !!caregiver?.email;
  const [locking, setLocking] = useState(false);
  const [resending, setResending] = useState(false);

  const resendInvite = async () => {
    setResending(true);
    try {
      const data = await caregiverService.resendInvite(caregiverID);
      toast.success(data?.detail || 'Invite re-sent.');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(detail || 'Failed to re-send invite.');
    } finally {
      setResending(false);
    }
  };

  const lockAccount = async () => {
    if (!confirm(`Lock ${cgName}'s account? They'll need to contact you for a PIN reset to regain access.`)) {
      return;
    }
    setLocking(true);
    try {
      await caregiverService.lock(caregiverID);
      toast.success('Account locked.');
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error;
      toast.error(detail || 'Failed to lock account.');
    } finally {
      setLocking(false);
    }
  };

  const save = async () => {
    const errors = {};
    if (!form.first_name?.trim()) errors.first_name = 'First name is required.';
    if (!form.last_name?.trim()) errors.last_name = 'Last name is required.';
    if (form.email?.trim() && !isValidEmail(form.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
    if (form.phone?.trim() && !isValidPhone(form.phone)) {
      errors.phone = 'Enter a valid phone number (at least 7 digits).';
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      await caregiverService.update(caregiverID, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email || null,
        phone: form.phone || null,
      });
      // Per-link relationship_type lives on the link, not the caregiver. Only
      // patch when the SC actually changed it and we have a link in context.
      if (linkRow && form.relationship_type && form.relationship_type !== linkRow.relationship_type) {
        await caregiverService.updateLink(caregiverID, linkRow.link_id, {
          relationship_type: form.relationship_type,
        });
      }
      toast.success('Caregiver profile updated.');
      setEditing(false);
      await load();
    } catch (err) {
      console.error('save failed', err);
      toast.error(err?.response?.data?.detail || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const back = () => onBack && onBack();

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  if (!caregiver) {
    return (
      <div className="p-6 text-sm text-gray-700 dark:text-gray-300">
        Caregiver not found.{' '}
        <button onClick={back} className="link">
          Back
        </button>
      </div>
    );
  }

  const cgName =
    caregiver.full_name ||
    `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim() ||
    caregiver.caregiver_id;

  const inputClass = (hasError) => `input ${hasError ? 'input-error' : ''}`;
  const clearError = (field) => {
    if (formErrors[field]) {
      const next = { ...formErrors };
      delete next[field];
      setFormErrors(next);
    }
  };

  return (
    <div className="p-6">
      <button
        onClick={back}
        className="back-link"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Caregivers List
      </button>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-gray-50">{cgName}</h1>
          {isLocked && <CaregiverStatusBadge status="Locked" />}
        </div>
        {!editing ? (
          <div className="flex flex-wrap gap-2">
            {/* FECOA-1431: retrieve the setup QR for re-onboarding. No temp PIN
                is available after creation (it's hashed), so the QR encodes the
                Caregiver ID + sponsor only. Hidden for locked/deactivated. */}
            {linkActive && !isLocked && (
              <button
                type="button"
                onClick={() => setShowSetupQr(true)}
                className={`btn ${canResendInvite ? 'btn-secondary' : 'btn-primary'}`}
              >
                <QrCode className="h-4 w-4" />
                Show Setup QR
              </button>
            )}
            {canResendInvite && (
              <button
                type="button"
                onClick={resendInvite}
                disabled={resending}
                className="btn btn-primary"
              >
                {resending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Resend Invite
              </button>
            )}
            {isLocked && (
              <button
                type="button"
                onClick={() => setShowResetPin(true)}
                className="btn btn-primary"
              >
                <KeyRound className="h-4 w-4" />
                Reset PIN
              </button>
            )}
            {/* Manual lock — only meaningful for Active caregivers (those with their own PIN) */}
            {isActiveStatus && linkActive && (
              <button
                type="button"
                onClick={lockAccount}
                disabled={locking}
                className="btn btn-secondary"
              >
                <ShieldOff className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                Lock Account
              </button>
            )}
            {linkActive && (
              <button
                type="button"
                onClick={() => setShowDeactivate(true)}
                className="btn btn-danger-soft border border-danger-500/30"
              >
                <Unlink className="h-4 w-4" />
                Deactivate Link
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setFormErrors({});
                setEditing(true);
              }}
              className="btn btn-secondary"
            >
              <Edit2 className="h-4 w-4" />
              Edit Caregiver
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setFormErrors({});
                load();
              }}
              disabled={saving}
              className="btn btn-secondary"
            >
              <X className="h-4 w-4" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {editing && (
        <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-xs text-amber-800 dark:text-amber-200">
            <p className="font-semibold mb-1">Global Profile Update</p>
            <p>
              Updating contact information will update this caregiver&rsquo;s global profile across
              the entire database. Changes will affect all participants linked to this caregiver.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 rounded-xl border border-gray-200 bg-white p-4 sm:grid-cols-2 dark:border-gray-700 dark:bg-gray-800">
        <Field label="Caregiver ID">
          <span className="font-mono text-primary-700 dark:text-primary-300">{caregiver.caregiver_id}</span>
        </Field>
        <Field label="Link Status">
          <CaregiverStatusBadge status={status} />
        </Field>
        <Field label="Full Name">
          {editing ? (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <input
                  type="text"
                  value={form.first_name}
                  onChange={(e) => {
                    setForm({ ...form, first_name: e.target.value });
                    clearError('first_name');
                  }}
                  className={inputClass(!!formErrors.first_name)}
                />
                {formErrors.first_name && (
                  <p className="field-error">{formErrors.first_name}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  value={form.last_name}
                  onChange={(e) => {
                    setForm({ ...form, last_name: e.target.value });
                    clearError('last_name');
                  }}
                  className={inputClass(!!formErrors.last_name)}
                />
                {formErrors.last_name && (
                  <p className="field-error">{formErrors.last_name}</p>
                )}
              </div>
            </div>
          ) : (
            cgName
          )}
        </Field>
        <Field label="Relationship">
          {editing ? (
            <RelationshipTypeSelect
              value={form.relationship_type || ''}
              onChange={(v) => setForm({ ...form, relationship_type: v })}
            />
          ) : (
            linkRow?.relationship_type || '—'
          )}
        </Field>
        <Field label="Email">
          {editing ? (
            <>
              <input
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  clearError('email');
                }}
                className={inputClass(!!formErrors.email)}
              />
              {formErrors.email && (
                <p className="field-error">{formErrors.email}</p>
              )}
            </>
          ) : (
            caregiver.email || '—'
          )}
        </Field>
        <Field label="Phone">
          {editing ? (
            <>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={form.phone || ''}
                onChange={(e) => {
                  setForm({ ...form, phone: sanitizePhoneInput(e.target.value) });
                  clearError('phone');
                }}
                placeholder="(555) 555-5555"
                className={inputClass(!!formErrors.phone)}
              />
              {formErrors.phone && (
                <p className="field-error">{formErrors.phone}</p>
              )}
            </>
          ) : (
            caregiver.phone || '—'
          )}
        </Field>
      </div>

      {isLocked && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-rose-300 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10">
          <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-600 dark:text-rose-400" />
          <div className="text-xs text-rose-800 dark:text-rose-200">
            <p className="font-semibold mb-1">Account Locked</p>
            <p>
              This caregiver&rsquo;s account has been locked due to multiple failed login attempts.
              Click &ldquo;Reset PIN&rdquo; to unlock their account and allow them to set a new PIN.
            </p>
          </div>
        </div>
      )}

      {isPendingFirstLogin && linkActive && caregiver?.email && (
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/30 dark:bg-amber-500/10">
          <Send className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div className="text-xs text-amber-800 dark:text-amber-200">
            <p className="font-semibold mb-1">Awaiting First Login</p>
            <p>{describeInviteState(caregiver.latest_invite)}</p>
          </div>
        </div>
      )}

      {/* Link history metadata. Always show linked-by/linked-at; surface the
          deactivation row prominently when the link is no longer active. */}
      {linkRow && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Link History</p>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-gray-500 dark:text-gray-400">Linked at</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDateTime(linkRow.linked_at)}</dd>
            <dt className="text-gray-500 dark:text-gray-400">Linked by</dt>
            <dd className="text-gray-900 dark:text-gray-100">{userLabel(linkRow.linked_by)}</dd>
            {!linkRow.is_active && (
              <>
                <dt className="text-rose-700 dark:text-rose-300">Deactivated at</dt>
                <dd className="text-rose-700 dark:text-rose-200">{formatDateTime(linkRow.deactivated_at)}</dd>
                <dt className="text-rose-700 dark:text-rose-300">Deactivated by</dt>
                <dd className="text-rose-700 dark:text-rose-200">{userLabel(linkRow.deactivated_by)}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      {showSetupQr && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4 dark:bg-black/70">
          <div className="w-full max-w-md animate-slide-up overflow-hidden rounded-xl bg-white shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10">
            <div className="card-band flex-nowrap">
              <h2 className="card-band-title">Setup QR Code</h2>
              <button
                type="button"
                onClick={() => setShowSetupQr(false)}
                aria-label="Close"
                className="-mr-2 flex h-8 w-8 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-[18px] w-[18px]" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-4">
                Scan this QR code to access the caregiver setup information.
              </p>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-900/40">
                <CaregiverSetupQR
                  caregiverId={caregiver.caregiver_id}
                  studyCode={studyCode}
                  sponsorId={getActiveSponsorId()}
                />
              </div>
            </div>
            <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-3.5 dark:border-gray-700 dark:bg-gray-900/40">
              <button
                type="button"
                onClick={() => setShowSetupQr(false)}
                className="btn btn-primary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showResetPin && (
        <ResetPinModal
          caregiverId={caregiverID}
          caregiverLabel={`${cgName} (${caregiver.caregiver_id})`}
          onClose={() => setShowResetPin(false)}
          onResetComplete={() => {
            setShowResetPin(false);
            load();
          }}
        />
      )}

      {showDeactivate && linkRow && (
        <DeactivateLinkConfirm
          link={linkRow}
          caregiverId={caregiverID}
          onClose={() => setShowDeactivate(false)}
          onConfirmed={() => {
            setShowDeactivate(false);
            load();
          }}
        />
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
    <div className="text-sm text-gray-900 dark:text-gray-100">{children}</div>
  </div>
);

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
};

const userLabel = (user) => {
  if (!user) return 'Unknown';
  return user.full_name || user.email || 'Unknown';
};

const describeInviteState = (invite) => {
  if (!invite) {
    return 'No invite has been sent yet. Use "Resend Invite" to email the setup link.';
  }
  if (invite.used_at) {
    return 'A previous invite link was used but no PIN was set. Re-send to issue a fresh link.';
  }
  if (!invite.is_valid) {
    return 'The previous invite has expired. Re-send to issue a fresh 72-hour link.';
  }
  const expires = invite.expires_at ? new Date(invite.expires_at) : null;
  if (!expires || isNaN(expires.getTime())) {
    return 'An invite is outstanding.';
  }
  const hoursLeft = Math.max(0, Math.round((expires - Date.now()) / 3_600_000));
  return `Invite is active and expires in about ${hoursLeft} hour${hoursLeft === 1 ? '' : 's'}.`;
};

export default CaregiverDetail;
